import { useMemo } from "react";
import { Grid, Button, Dropdown } from "semantic-ui-react";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { ErrorInfo, FieldRow } from "components/form";
import { toast } from "components/Toast/Toast";
import { validateEmail, usersToSelectOptions } from "services/common";
import { AgencyType } from "models";

export const MediaAgencyForm = ({
  mediaAgencyToUpdate,
  closeMediaAgencyForm,
  users,
  dispatch,
  t,
}) => {
  const mediaAgencySchema = yup.object().shape({
    name: yup.string().required(t("required")),
    email: yup.string().required(t("required")).test("is-email", t("invalid_email"), validateEmail),
    contacts: yup.array().min(1, t("min_one_contact_error")),
  });

  const initialValues = useMemo(
    () =>
      !mediaAgencyToUpdate
        ? {
            name: "",
            email: "",
            contacts: [],
          }
        : {
            name: mediaAgencyToUpdate.name,
            email: mediaAgencyToUpdate.email,
            contacts: mediaAgencyToUpdate.contacts,
          },
    [mediaAgencyToUpdate]
  );

  const submitCreate = async (values) => {
    try {
      await dispatch.agencys.create({ ...values, type: AgencyType.MEDIA_AGENCY });
      toast(undefined, t("media_agency_create_success"));
    } catch (error) {
      console.error(error);
      toast("error", t("media_agency_create_failure"));
    }
    closeMediaAgencyForm();
  };

  const submitUpdate = async (values) => {
    try {
      await dispatch.agencys.update({ id: mediaAgencyToUpdate.id, ...values });
      toast(undefined, t("media_agency_update_success"));
    } catch (error) {
      console.error(error);
      toast("error", t("media_agency_update_failure"));
    }
    closeMediaAgencyForm();
  };

  return (
    <Formik
      validationSchema={mediaAgencySchema}
      initialValues={initialValues}
      onSubmit={mediaAgencyToUpdate ? submitUpdate : submitCreate}
    >
      {({ values, errors, isSubmitting, setFieldValue, submitCount }) => (
        <Form>
          <h3>{mediaAgencyToUpdate ? t("media_agency_update") : t("media_agency_create")}:</h3>
          <Grid columns={2} verticalAlign="middle">
            <FieldRow label={t("name")} name="name" />
            <FieldRow label={t("email")} name="email"></FieldRow>
            <Grid.Row className="form-field-row">
              <Grid.Column width={3} textAlign="right">
                <label htmlFor="contacts">{t("contacts")}</label>
              </Grid.Column>
              <Grid.Column width={13}>
                <Dropdown
                  name="contacts"
                  placeholder={t("contacts_placeholder")}
                  value={values.contacts}
                  fluid
                  multiple
                  search
                  selection
                  options={usersToSelectOptions(users)}
                  onChange={(_e, { value, name }) => setFieldValue(name, value)}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <ErrorInfo errors={errors} submitCount={submitCount} t={t} tPrefix={""} />
          <div className="form-footer">
            <Button type="button" primary onClick={() => closeMediaAgencyForm()}>
              {t("cancel")}
            </Button>
            <Button primary type="submit" loading={isSubmitting}>
              {mediaAgencyToUpdate ? t("update") : t("create")}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
